import { DEFAULT_TIME_RANGE, RANGE_TYPE_LABELS } from "./constants";
import Highcharts from "highcharts/highstock";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export function getRange(value = DEFAULT_TIME_RANGE) {
  const now = dayjs();
  switch (value) {
    case RANGE_TYPE_LABELS.HOUR_1:
      return [now.subtract(1, "hour"), now];
    case RANGE_TYPE_LABELS.HOUR_2:
      return [now.subtract(2, "hour"), now];
    case RANGE_TYPE_LABELS.HOUR_12:
      return [now.subtract(12, "hour"), now];
    case RANGE_TYPE_LABELS.HOUR_24:
      return [now.subtract(24, "hour"), now];
    case RANGE_TYPE_LABELS.DAY_1:
      return [now.startOf("day"), now];
    case RANGE_TYPE_LABELS.DAY_MINUS_1: {
      const yesterday = now.subtract(1, "day");
      return [yesterday.startOf("day"), yesterday.endOf("day")];
    }
    case RANGE_TYPE_LABELS.DAY_7:
      return [now.subtract(6, "day").startOf("day"), now];
    case RANGE_TYPE_LABELS.CUSTOM:
    default:
      return [now.subtract(12, "hour"), now];
  }
}
export function applyTheme(theme) {
  document.body.classList.remove("theme-light", "theme-dark");
  document.documentElement.classList.remove("light", "dark");
  document.body.classList.add(`theme-${theme}`);
  document.documentElement.setAttribute("data-theme", theme);
  document.documentElement.classList.add(theme);
}

export const getDateRangeFormat = range => {
  return Array.isArray(range) && range.length > 0 ? range.map(d => d.format("MMM-DD, YYYY, hh:mm A")).join(" - ") : "";
};

// utils/transformChargerData.js
export function transformChargerData(apiData = [], startTime, endTime) {
  // 0. Sort apiData by generatedAt timestamp (ascending order)
  apiData = [...(apiData || [])].sort((a, b) => {
    const timeA = a?.generatedAt ? dayjs(a.generatedAt).valueOf() : 0;
    const timeB = b?.generatedAt ? dayjs(b.generatedAt).valueOf() : 0;
    return timeA - timeB;
  });

  // 1. Filter schedules >= generatedAt and normalize setpointPower
  apiData = apiData?.map(slots => ({
    ...slots,
    schedule: slots?.schedule?.filter(schedule => schedule?.timestamp >= slots?.generatedAt),
  }));

  const finalData = [];

  // 2. Build continuous data from overlapping schedules
  for (let i = 0; i < apiData?.length - 1; i++) {
    const currentSchedules = apiData[i]?.schedule;
    const nextSchedules = apiData[i + 1]?.schedule;

    for (let j = 0; j < currentSchedules?.length; j++) {
      if (currentSchedules[j]?.timestamp >= nextSchedules?.[0]?.timestamp) {
        break;
      }

      finalData.push([
        new Date(currentSchedules[j]?.timestamp).getTime(),
        currentSchedules[j]?.value?.setpointPower ?? 0,
        {
          ...apiData[i]?.calculationMetrics,
          type: apiData[i]?.scheduleOptimisationType,
        },
      ]);
    }
  }

  // 3. Append last schedule fully
  const lastSchedule = apiData[apiData?.length - 1]?.schedule;
  for (let i = 0; i < lastSchedule?.length; i++) {
    finalData.push([
      new Date(lastSchedule[i]?.timestamp).getTime(),
      lastSchedule[i]?.value?.setpointPower ?? 0,
      {
        ...apiData[apiData.length - 1]?.calculationMetrics,
        type: apiData[apiData.length - 1]?.scheduleOptimisationType,
      },
    ]);
  }

  if (!finalData.length) {
    return { setpointPower: [] };
  }

  const fiveMinutesEpoch = 300_000;
  const fifteenMinutesEpoch = 900_000;

  const finalInjectedData = [];

  function getDefaultZeroOptimisation(baseTimestamp, templateMeta = {}) {
    // When we inject a "zero" point, we also want all calculationMetrics fields to be 0.
    // Keep non-metric identifiers like `type` intact.
    const zeroedMeta = {};
    for (const [key, value] of Object.entries(templateMeta || {})) {
      if (key === "type") {
        zeroedMeta[key] = value;
      } else if (typeof value === "number") {
        zeroedMeta[key] = 0;
      } else if (typeof value === "boolean") {
        zeroedMeta[key] = false;
      } else if (value == null) {
        // treat missing metric values as 0 for injected points
        zeroedMeta[key] = 0;
      } else {
        // fallback: preserve as-is (in case a non-metric field exists)
        zeroedMeta[key] = value;
      }
    }

    return [baseTimestamp, 0, zeroedMeta];
  }

  // 4. Use Set to track existing timestamps (performance fix)
  const existingTimestamps = new Set(finalData.map(item => item[0]));

  // 5. Inject missing zero points when gap > threshold
  for (let i = 0; i < finalData.length; i++) {
    const current = finalData[i];
    const currentTs = current[0];
    const currentMeta = current[2] ?? {};
    const nextTs = finalData[i + 1]?.[0] ?? null;

    // always push original point
    finalInjectedData.push(current);

    const isSmart = currentMeta?.type === "HemsOptimisation";
    const threshold = isSmart ? fifteenMinutesEpoch : fiveMinutesEpoch;
    const candidateTs = currentTs + threshold;

    const shouldInject = nextTs === null ? true : nextTs > candidateTs;
    const alreadyExists = existingTimestamps.has(candidateTs);

    if (shouldInject && !alreadyExists) {
      finalInjectedData.push(getDefaultZeroOptimisation(candidateTs, currentMeta));
      existingTimestamps.add(candidateTs);
    }
  }

  const smartChargingSetpointPower = finalInjectedData
    .filter(item => item[2]?.type === "HemsOptimisation")
    .filter(item => {
      if (!endTime) return true;
      const endTimestampUTC = endTime.valueOf();
      return item[0] <= endTimestampUTC;
    });
  const greenChargingSetpointPower = finalInjectedData.filter(item => item[2]?.type === "GreenCharging");

  return {
    smartChargingSetpointPower,
    greenChargingSetpointPower,
  };
}

export function getEnergyConsumptionDateFormat(date) {
  return (
    date.getUTCFullYear().toString() +
    String(date.getUTCMonth() + 1).padStart(2, "0") +
    String(date.getUTCDate()).padStart(2, "0")
  );
}

export function transformEnergyConsumptionData(data, startDate, endDate, startTime, endTime) {
  if (!Array.isArray(data) || !startTime || !endTime) return [];

  const FIVE_MIN_MS = 5 * 60 * 1000;
  const startUTC = dayjs(startTime).utc();
  const endUTC = dayjs(endTime).utc();

  const baseDayUTC = startUTC.startOf("day");
  const baseTs = baseDayUTC.valueOf();

  const startEpoch = startUTC.valueOf();
  const endEpoch = endUTC.valueOf();

  let filteredData = data;
  if (startDate === endDate) {
    const nowUTC = dayjs.utc();

    let points = Math.floor(nowUTC.diff(baseDayUTC, "minute") / 5);

    if (!Number.isFinite(points) || points <= 0) {
      points = data.length;
    } else if (points > data.length) {
      points = data.length;
    }

    filteredData = data.slice(-points);
  }

  const built = filteredData.map((value, index) => {
    const ts = baseTs + index * FIVE_MIN_MS - FIVE_MIN_MS * 12;
    return [ts, value];
  });

  const finalData = built.filter(([ts]) => {
    return ts >= startEpoch && ts <= endEpoch;
  });
  return finalData;
}

export const getMaxTime = date => {
  const now = dayjs();
  if (date && dayjs(date).isSame(now, "day")) {
    return now;
  }
  return dayjs().endOf("day");
};

export function formatDate(value) {
  if (value === null || value === undefined || value === "") return "-";
  // Pre-clean common non-ISO suffixes like [UTC] so Date can parse the string
  let cleaned = value;
  try {
    if (typeof cleaned === "string") {
      // remove trailing bracketed zone like [UTC]
      cleaned = cleaned.replace(/\[.*\]$/, "").trim();
      // if still contains a space-separated timezone token at end (e.g. "...Z UTC"), drop it
      const parts = cleaned.split(" ");
      if (parts.length > 1 && /[A-Za-z]{2,}/.test(parts[parts.length - 1])) {
        parts.pop();
        cleaned = parts.join(" ");
      }
    }
  } catch (e) {
    // fall through to parse attempt
  }

  // Try to construct a Date and if invalid, fall back to the raw value
  const d = new Date(cleaned);
  if (isNaN(d.getTime())) return String(value);
  // Format: DD/MM/YYYY HH:MM:SS TIMEZONE (timezone as GMT±HH:MM)
  const pad = n => String(n).padStart(2, "0");
  const DD = pad(d.getDate());
  const MM = pad(d.getMonth() + 1);
  const YYYY = d.getFullYear();
  const hh = pad(d.getHours());
  const mm = pad(d.getMinutes());
  const ss = pad(d.getSeconds());
  // timezone offset in minutes; positive east of UTC
  const offsetMinutes = -d.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const offHours = pad(Math.floor(Math.abs(offsetMinutes) / 60));
  const offMins = pad(Math.abs(offsetMinutes) % 60);
  const tz = `GMT${sign}${offHours}:${offMins}`;
  return `${DD}/${MM}/${YYYY} ${hh}:${mm}:${ss} ${tz}`;
}

export const initModule = module => {
  if (typeof module === "function") {
    module(Highcharts);
  } else if (module && module.default) {
    module.default(Highcharts);
  }
};

export const getTimezoneLabel = () => {
  const offset = new Date().getTimezoneOffset();
  const sign = offset <= 0 ? "+" : "-";
  const absOffset = Math.abs(offset);
  const hours = String(Math.floor(absOffset / 60)).padStart(2, "0");
  const minutes = String(absOffset % 60).padStart(2, "0");
  return `UTC${sign}${hours}:${minutes}`;
};

export function calculateOnlineOfflinePercent(data, dateRange) {
  let percentOnline = 0;
  let percentOffline = 0;
  if (data && data.length > 0) {
    let onlineDuration = 0;
    let offlineDuration = 0;
    let totalDuration = 0;
    const sortedData = [...data].sort((a, b) => a.x - b.x);
    let startTime = sortedData[0].x;
    let endTime = sortedData[sortedData.length - 1].x;
    if (dateRange?.length === 2) {
      const d1 = toEpoch(dateRange[0]);
      const d2 = toEpoch(dateRange[1]);
      startTime = Math.min(d1, d2);
      endTime = Math.max(d1, d2);
    }
    for (let i = 0; i < sortedData.length - 1; i++) {
      const curr = sortedData[i];
      const next = sortedData[i + 1];
      const segStart = Math.max(curr.x, startTime);
      const segEnd = Math.min(next.x, endTime);
      if (segEnd > segStart) {
        const duration = segEnd - segStart;
        if (curr.y === 1) onlineDuration += duration;
        else offlineDuration += duration;
        totalDuration += duration;
      }
    }
    const last = sortedData[sortedData.length - 1];
    if (last.x < endTime) {
      const duration = endTime - last.x;
      if (last.y === 1) onlineDuration += duration;
      else offlineDuration += duration;
      totalDuration += duration;
    }
    if (totalDuration > 0) {
      percentOnline = Number(((onlineDuration / totalDuration) * 100).toFixed(1));
      percentOffline = Number(((offlineDuration / totalDuration) * 100).toFixed(1));
    }
  }
  return { percentOnline, percentOffline };
}

export const toEpoch = val => {
  if (typeof val === "number") return val;
  const d = new Date(val);
  return isNaN(d.getTime()) ? val : d.getTime();
};

export function getTimezoneOffset(timeZone, date = new Date()) {
  try {
    // We use 'longOffset' to get the format "GMT+05:30"
    const options = { timeZone, timeZoneName: "longOffset" };

    // formatToParts gives us specific segments of the date
    const formatter = new Intl.DateTimeFormat("en-US", options);
    const parts = formatter.formatToParts(date);

    // Find the part that contains the timezone info
    const timeZoneName = parts.find(part => part.type === "timeZoneName").value;

    // Remove "GMT" prefix to get just "+05:30"
    return timeZoneName;
  } catch (error) {
    console.error(`Invalid timezone: ${timeZone}`, error);
    return "";
  }
}

export const getCopyrightText = (startYear = 2008) => {
  const currentYear = new Date().getFullYear();
  return `© ${startYear}-${currentYear} Enphase Energy Inc. All rights reserved.`;
};
